import React from 'react';
import './Contact.css';

const Contact = () => {
/* --- [RESERVATION FORM STATE & HELPERS] Uncomment if re-enabling Book a Table form ---
  const getTodayDateString = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const getCurrentTimeString = () => {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  // State variables
  const [date, setDate] = useState(getTodayDateString());
  const [time, setTime] = useState('');
  const [minDate, setMinDate] = useState(getTodayDateString());
  const [minTime, setMinTime] = useState(getCurrentTimeString());

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isTimeOpen, setIsTimeOpen] = useState(false);
  
  const [calendarMonth, setCalendarMonth] = useState(new Date().getMonth());
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());

  const datePickerRef = useRef(null);
  const timePickerRef = useRef(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Refresh current time min limits
  useEffect(() => {
    const interval = setInterval(() => {
      const todayStr = getTodayDateString();
      setMinDate(todayStr);
      setMinTime(getCurrentTimeString());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Reset time if selected date is today and selected time is in the past
  useEffect(() => {
    if (date === minDate && time) {
      const currentTime = getCurrentTimeString();
      if (time < currentTime) {
        setTime('');
      }
    }
  }, [date, time, minDate]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setIsCalendarOpen(false);
      }
      if (timePickerRef.current && !timePickerRef.current.contains(e.target)) {
        setIsTimeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Date helper methods
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const getDayOfWeek = (dateString) => {
    if (!dateString) return -1;
    const parts = dateString.split('-');
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    return d.getDay();
  };

  const isDayInPast = (d) => {
    const cellDateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const todayStr = getTodayDateString();
    return cellDateStr < todayStr;
  };

  const isDaySelected = (d) => {
    const cellDateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    return date === cellDateStr;
  };

  const isPrevMonthDisabled = () => {
    const today = new Date();
    return calendarYear < today.getFullYear() || (calendarYear === today.getFullYear() && calendarMonth <= today.getMonth());
  };

  const prevMonth = (e) => {
    e.preventDefault();
    if (isPrevMonthDisabled()) return;
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else {
      setCalendarMonth(calendarMonth - 1);
    }
  };

  const nextMonth = (e) => {
    e.preventDefault();
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(calendarMonth + 1);
    }
  };

  const handleSelectDay = (d) => {
    const selectedDateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    setDate(selectedDateStr);
    setIsCalendarOpen(false);
  };

  const handleSelectTime = (t) => {
    setTime(t);
    setIsTimeOpen(false);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'Select Date';
    const parts = dateString.split('-');
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const formatDisplayTime = (timeString) => {
    if (!timeString) return 'Select Time';
    const parts = timeString.split(':');
    let hh = parseInt(parts[0], 10);
    const mm = parts[1];
    const ampm = hh >= 12 ? 'PM' : 'AM';
    hh = hh % 12;
    hh = hh ? hh : 12;
    return `${String(hh).padStart(2, '0')}:${mm} ${ampm}`;
  };

  // Generate available time slots based on Pyro Pot operating hours
  const getTimeSlotsForDate = (dateString) => {
    const day = getDayOfWeek(dateString);
    let slots = [];
    
    if (day === 0) { // Sunday: 4:00 PM - 10:00 PM
      slots = [
        { value: '16:00', label: '04:00 PM' },
        { value: '16:30', label: '04:30 PM' },
        { value: '17:00', label: '05:00 PM' },
        { value: '17:30', label: '05:30 PM' },
        { value: '18:00', label: '06:00 PM' },
        { value: '18:30', label: '06:30 PM' },
        { value: '19:00', label: '07:00 PM' },
        { value: '19:30', label: '07:30 PM' },
        { value: '20:00', label: '08:00 PM' },
        { value: '20:30', label: '08:30 PM' },
        { value: '21:00', label: '09:00 PM' },
        { value: '21:30', label: '09:30 PM' },
        { value: '22:00', label: '10:00 PM' },
      ];
    } else if (day === 5 || day === 6) { // Fri - Sat: 5:00 PM - 12:00 AM
      slots = [
        { value: '17:00', label: '05:00 PM' },
        { value: '17:30', label: '05:30 PM' },
        { value: '18:00', label: '06:00 PM' },
        { value: '18:30', label: '06:30 PM' },
        { value: '19:00', label: '07:00 PM' },
        { value: '19:30', label: '07:30 PM' },
        { value: '20:00', label: '08:00 PM' },
        { value: '20:30', label: '08:30 PM' },
        { value: '21:00', label: '09:00 PM' },
        { value: '21:30', label: '09:30 PM' },
        { value: '22:00', label: '10:00 PM' },
        { value: '22:30', label: '10:30 PM' },
        { value: '23:00', label: '11:00 PM' },
        { value: '23:30', label: '11:30 PM' },
      ];
    } else { // Mon - Thu: 5:00 PM - 11:00 PM
      slots = [
        { value: '17:00', label: '05:00 PM' },
        { value: '17:30', label: '05:30 PM' },
        { value: '18:00', label: '06:00 PM' },
        { value: '18:30', label: '06:30 PM' },
        { value: '19:00', label: '07:00 PM' },
        { value: '19:30', label: '07:30 PM' },
        { value: '20:00', label: '08:00 PM' },
        { value: '20:30', label: '08:30 PM' },
        { value: '21:00', label: '09:00 PM' },
        { value: '21:30', label: '09:30 PM' },
        { value: '22:00', label: '10:00 PM' },
        { value: '22:30', label: '10:30 PM' },
        { value: '23:00', label: '11:00 PM' },
      ];
    }

    if (dateString === minDate) {
      return slots.map(slot => ({
        ...slot,
        disabled: slot.value < minTime
      }));
    }
    return slots.map(slot => ({ ...slot, disabled: false }));
  };

  const currentSlots = getTimeSlotsForDate(date);
  ---------------------------------------------------------------------------------------- */

  return (
    <section id="contact" className="contact-section">
      <div className="container">
        <h2 className="section-title">Contact Us</h2>
        
        <div className="contact-container">
          {/* <div className="contact-form-container">
            <h3 className="sub-title">Book a Table</h3>
            <form className="contact-form" onSubmit={(e) => e.preventDefault()}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <input type="text" id="name" placeholder="Your Name" required />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Phone</label>
                  <input type="tel" id="phone" placeholder="Your Phone Number" required />
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group" ref={datePickerRef}>
                  <label>Date</label>
                  <div className="custom-picker-container">
                    <div 
                      className={`custom-input-trigger ${isCalendarOpen ? 'active' : ''}`}
                      onClick={() => {
                        setIsCalendarOpen(!isCalendarOpen);
                        setIsTimeOpen(false);
                      }}
                    >
                      <span className="trigger-value">{formatDisplayDate(date)}</span>
                      <span className="trigger-icon">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display: 'block'}}>
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                      </span>
                    </div>
                    
                    {isCalendarOpen && (
                      <div className="custom-calendar-dropdown">
                        <div className="calendar-header">
                          <button 
                            className="cal-nav-btn" 
                            onClick={prevMonth}
                            disabled={isPrevMonthDisabled()}
                          >
                            &lt;
                          </button>
                          <span className="cal-month-title">
                            {months[calendarMonth]} {calendarYear}
                          </span>
                          <button className="cal-nav-btn" onClick={nextMonth}>
                            &gt;
                          </button>
                        </div>
                        
                        <div className="calendar-weekdays">
                          <span>Su</span>
                          <span>Mo</span>
                          <span>Tu</span>
                          <span>We</span>
                          <span>Th</span>
                          <span>Fr</span>
                          <span>Sa</span>
                        </div>
                        
                        <div className="calendar-days-grid">
                          {Array.from({ length: getFirstDayOfMonth(calendarMonth, calendarYear) }).map((_, i) => (
                            <span key={`empty-${i}`} className="calendar-day empty"></span>
                          ))}
                          
                          {Array.from({ length: getDaysInMonth(calendarMonth, calendarYear) }).map((_, i) => {
                            const d = i + 1;
                            const isPast = isDayInPast(d);
                            const isSelected = isDaySelected(d);
                            return (
                              <button
                                key={`day-${d}`}
                                type="button"
                                className={`calendar-day ${isPast ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
                                disabled={isPast}
                                onClick={() => handleSelectDay(d)}
                              >
                                {d}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  <input type="hidden" name="date" value={date} required />
                </div>

                <div className="form-group" ref={timePickerRef}>
                  <label>Time</label>
                  <div className="custom-picker-container">
                    <div 
                      className={`custom-input-trigger ${isTimeOpen ? 'active' : ''}`}
                      onClick={() => {
                        setIsTimeOpen(!isTimeOpen);
                        setIsCalendarOpen(false);
                      }}
                    >
                      <span className="trigger-value">{formatDisplayTime(time)}</span>
                      <span className="trigger-icon">
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display: 'block'}}>
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                      </span>
                    </div>
                    
                    {isTimeOpen && (
                      <div className="custom-time-dropdown">
                        {currentSlots.map(slot => (
                          <button
                            key={slot.value}
                            type="button"
                            className={`time-slot-option ${slot.disabled ? 'disabled' : ''} ${time === slot.value ? 'selected' : ''}`}
                            disabled={slot.disabled}
                            onClick={() => handleSelectTime(slot.value)}
                          >
                            {slot.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input type="hidden" name="time" value={time} required />
                </div>

                <div className="form-group">
                  <label htmlFor="guests">Guests</label>
                  <select id="guests">
                    <option value="1">1 Person</option>
                    <option value="2">2 People</option>
                    <option value="3">3 People</option>
                    <option value="4">4 People</option>
                    <option value="5">5 People</option>
                    <option value="6+">6+ People</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Special Requests</label>
                <textarea id="message" rows="4" placeholder="Any dietary requirements or special occasions?"></textarea>
              </div>

              <button type="submit" className="btn btn-primary btn-submit">Confirm Reservation</button>
            </form>
          </div> */}

          <div className="contact-info">
            <div className="info-card">
              <h3 className="sub-title">Location</h3>
              <p>9804 S Yosemite St<br />Lone Tree, CO 80124</p>
              
              <div className="hours-block mt-4">
                <h4 className="hours-title">Lunch Hours</h4>
                <div className="hours-row">
                  <span>Sun - Sat</span>
                  <span>11:00 AM - 3:00 PM</span>
                </div>
              </div>
              <div className="hours-block mt-4">
                <h4 className="hours-title">Dinner Hours</h4>
                <div className="hours-row">
                  <span>Sun - Thurs</span>
                  <span>4:00 PM - 9:30 PM</span>
                </div>
                <div className="hours-row">
                  <span>Fri - Sat</span>
                  <span>4:00 PM - 10:00 PM</span>
                </div>
              </div>              

              <div className="contact-details mt-4">
                <p><strong>Phone:</strong> (720) 290-1851</p>
              </div>
            </div>
          </div>

          <div className="map-container">
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3076.9875263224235!2d-104.8850319!3d39.53733539999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x876c851b7373c241%3A0x355b53b61a209cdf!2s9804%20S%20Yosemite%20St%2C%20Lone%20Tree%2C%20CO%2080124!5e0!3m2!1sen!2sus!4v1780729266350!5m2!1sen!2sus" 
              width="100%" 
              height="100%" 
              style={{border: 0, borderRadius: '8px'}} 
              allowFullScreen="" 
              loading="lazy"
              title="Google Maps Location"
            ></iframe>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
